import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {Case, formatItem, OccupancyType} from "./types";
import {
    BoxAccess,
    BoxTypeSelection, BoxTypeSelectionResult,
    classNames,
    layout,
    MessageView,
    PendingView,
    PushButton,
    typography,
    useAsync,
    useBoxes,
    usePlatform,
    usePreventDeactivate,
    View,
    ViewContent,
    ViewHeader,
    ViewTitle
} from "@variocube/cube-ui";
import {getBoxTypes} from "@variocube/app-common";
import {Box, LogLevel, Occupancy} from "@variocube/app-api";
import {useLocalization} from "../i18n";

interface StorageFlowProps {
    storageCase: Case;
    onDone: () => void;
}

export function StorageFlow({storageCase, onDone}: StorageFlowProps) {
    const [boxSelection, setBoxSelection] = useState<BoxTypeSelectionResult>();
    const {t} = useLocalization();
    const boxes = useBoxes();
    const platform = usePlatform();

    const {error, loading, result: occupancy} = useAsync(async () => {
        if (storageCase.box) {
            return await platform.occupy({
                boxNumber: storageCase.box.name,
                accessCode: storageCase.pickupCode,
                content: {
                    occupancy: storageCase
                },
            })
        } else if (boxSelection) {
            return await platform.occupy({
                ...boxSelection,
                accessCode: storageCase.pickupCode,
                content: {
                    occupancy: storageCase
                },
            });
        }
    }, [platform, storageCase, boxSelection]);

    const box = useMemo(() => boxes.find(box => box.number == occupancy?.boxNumber), [boxes, occupancy]);
    const handleOtherTypeClick = useCallback(() => setBoxSelection(undefined), []);

    if (!storageCase.box && !boxSelection) {
        const types = getBoxTypes(boxes);
        return (
            <View className={layout.vertical}>
                <ViewHeader className={classNames(layout.paddingMedium, typography.alignCenter)}>
                    <ViewTitle>
                        {t("SelectTypeTitle")}
                    </ViewTitle>
                </ViewHeader>
                <ViewContent>
                    <BoxTypeSelection onSelect={setBoxSelection} types={types} strings={t} />
                </ViewContent>
            </View>
        )
    }
    else if (loading) {
        return <PendingView message={t("OccupyPending")}/>
    }
    else if (error) {
        return (
            <MessageView message={t("BoxTypeNotAvailable")}>
                <PushButton onClick={handleOtherTypeClick}>
                    {t("SelectOtherSize")}
                </PushButton>
                <PushButton onClick={onDone}>
                    {t("Cancel")}
                </PushButton>
            </MessageView>
        )
    }
    else if (occupancy && box) {
        return (
            <StorageBoxAccess
                occupancy={occupancy}
                box={box}
                onDone={onDone}
                storageCase={storageCase}
            />
        );
    }
    return (
        <MessageView message={"Internal error"}>
            <PushButton onClick={onDone}>
                {t("Cancel")}
            </PushButton>
        </MessageView>
    );
}

interface StorageBoxAccessProps {
    occupancy: Occupancy;
    box: Box;
    storageCase: Case;
    onDone: () => void;
}

function StorageBoxAccess({occupancy, box, storageCase, onDone}: StorageBoxAccessProps) {

    const platform = usePlatform();
    const {t} = useLocalization();

    const handleConfirm = useCallback(async () => {
        try {
            await platform.confirmOccupancy(occupancy.uuid);
            await platform.log(LogLevel.Audit, "Storage", {storageCase});
            onDone();
        }
        catch (error) {
            console.error("Error confirming occupancy", error);
        }
    }, [platform, occupancy, onDone]);

    const handleCancel = useCallback(async () => {
        try {
            await platform.cancelOccupancy(occupancy.uuid);
            onDone();
        }
        catch (error) {
            console.error("Error cancelling occupancy", error);
        }

    }, [platform, occupancy, onDone]);

    usePreventDeactivate();

    const itemName = formatItem(t("StoreItemUnknownItem"), storageCase.description, storageCase.item);
    const storeItemMessage = (storageCase.type == OccupancyType.Outbound && storageCase.customerName) ?
        t("StoreItemMessageWithCustomer", {item: itemName, customerName: storageCase.customerName, boxNumber: box.number}) :
        t("StoreItemMessage", {item: itemName, boxNumber: box.number});

    return (
        <BoxAccess
            boxes={box}
            onCancel={handleCancel}
            allowCancelWhenOpen
            platform={platform}
            onConfirm={handleConfirm}
            boxOpenTitle={t("StoreItem")}
            boxOpenMessage={storeItemMessage}
            confirmTitle={t("ConfirmStorage")}
            confirmMessage={t("ConfirmStorageMessage", {item: itemName, boxNumber: box.number})}
            strings={t}
        />
    );
}
